import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../context/AuthContext';
import PaymentModal from '../../components/PaymentModal';
import CustomAlert from '../../components/CustomAlert';

interface SubscriptionPlan {
  _id: string;
  name: string;
  price: number;
  duration: number;
  features: string[];
  isPopular: boolean;
  isActive: boolean;
}

const Pricing: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [userSubscription, setUserSubscription] = useState<any>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({ title: '', message: '', type: 'info' });
  const { isAuthenticated, user, setUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated) {
      fetchUserSubscription();
    }
  }, [isAuthenticated]);

  // Debug: Log subscription data when it changes
  useEffect(() => {
    // console.log('🔍 Subscription data changed:', userSubscription);
  }, [userSubscription]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/subscriptions/plans');
      // Ensure we only show exactly 3 plans
      const limitedPlans = response.data.slice(0, 3);
      // console.log('🔍 Fetched plans:', limitedPlans.map((p: SubscriptionPlan) => ({ name: p.name, price: p.price })));
      setPlans(limitedPlans);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/status/my-subscription');
      // console.log('🔍 User subscription data:', response.data);
      // console.log('🔍 Subscription tier:', response.data?.tier);
      // console.log('🔍 Subscription isActive:', response.data?.isActive);
      setUserSubscription(response.data);
    } catch (err) {
      console.error('Error fetching user subscription:', err);
      // User might not have a subscription yet, which is fine
      setUserSubscription(null);
    }
  };

  const showAlertModal = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setAlertConfig({ title, message, type });
    setShowAlert(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const formatDuration = (duration: number) => {
    // Handle days (for free plan)
    if (duration <= 30) {
      if (duration === 1) return '1 day';
      if (duration === 7) return '7 days';
      return `${duration} days`;
    }
    
    // Handle months
    if (duration === 1) return '1 month';
    if (duration < 12) return `${duration} months`;
    
    // Handle years
    const years = Math.floor(duration / 12);
    const months = duration % 12;
    if (months === 0) return `${years} year${years > 1 ? 's' : ''}`;
    return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
  };

  const handleGetStarted = async (plan: SubscriptionPlan) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user already has this plan
    // console.log('🔍 Checking plan:', plan.name);
    // console.log('🔍 User subscription:', userSubscription);
    // console.log('🔍 User subscription tier:', userSubscription?.tier);
    // console.log('🔍 User subscription isActive:', userSubscription?.isActive);
    
    let isCurrentPlan = (userSubscription && userSubscription.tier === plan.name && userSubscription.isActive) ||
                       (!userSubscription && plan.name === 'Free');
    
    // console.log('🔍 Initial isCurrentPlan:', isCurrentPlan);
    
    // Fallback: Check user data if subscription data is not available
    if (!isCurrentPlan && user) {
      const userSubscriptionPlan = user.subscriptionPlan;
      const isSubscribed = user.isSubscribed;
      
      // console.log('🔍 User subscription plan:', userSubscriptionPlan);
      // console.log('🔍 User is subscribed:', isSubscribed);
      
      // If user has an active subscription, check if this plan matches
      if (isSubscribed && userSubscriptionPlan === plan.name) {
        isCurrentPlan = true;
        // console.log('🔍 Set isCurrentPlan to true via user data');
      } else if (!isSubscribed && plan.name === 'Free') {
        isCurrentPlan = true;
        // console.log('🔍 Set isCurrentPlan to true for Free plan');
      }
    }
    
    // console.log('🔍 Final isCurrentPlan:', isCurrentPlan);
    
    if (isCurrentPlan) {
      showAlertModal(
        'Already Subscribed',
        `You are already on the ${plan.name} plan.`,
        'warning'
      );
      return;
    }

    // For free plans, subscribe directly
    if (plan.name === 'Free' || plan.price === 0) {
      try {
        setSubscribing(plan._id);
        const response = await api.post(`/subscriptions/subscribe/${plan._id}`, {
          momoNumber: undefined
        });
        
        if (response.data) {
          showAlertModal(
            'Subscription Successful!',
            'Successfully subscribed to Free plan!',
            'success'
          );
          fetchUserSubscription(); // Refresh subscription data
          
          // Update user data with new subscription info
          if (user) {
            const updatedUser = {
              ...user,
              isSubscribed: false, // Free plan
              subscriptionPlan: 'Free',
              tier: 'Free',
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          }
          
          // Redirect to learning dashboard after successful subscription
          setTimeout(() => {
            navigate('/learning');
          }, 2000); // Give user time to see the success message
        }
      } catch (error: any) {
        console.error('Subscription error:', error);
        const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
        showAlertModal(
          'Subscription Failed',
          errorMessage,
          'error'
        );
      } finally {
        setSubscribing(null);
      }
    } else {
      // For paid plans, show payment modal
      setSelectedPlan(plan);
      setShowPaymentModal(true);
    }
  };

  const handlePaymentConfirm = async (countryCode: string, phoneNumber: string) => {
    if (!selectedPlan) return;

    try {
      setSubscribing(selectedPlan._id);
      const fullPhoneNumber = `${countryCode}${phoneNumber}`;
      const response = await api.post(`/subscriptions/subscribe/${selectedPlan._id}`, {
        momoNumber: fullPhoneNumber
      });
      
      if (response.data.paymentUrl) {
        // Redirect to payment page
        window.open(response.data.paymentUrl, '_blank');
        setShowPaymentModal(false);
        setSelectedPlan(null);
        showAlertModal(
          'Payment Required',
          'Please complete payment in the opened window to activate your subscription.',
          'info'
        );
      } else {
        showAlertModal(
          'Subscription Successful!',
          'Subscription initiated successfully!',
          'success'
        );
        setShowPaymentModal(false);
        setSelectedPlan(null);
        fetchUserSubscription(); // Refresh subscription data
        
        // Update user data with new subscription info
        if (user && selectedPlan) {
          const updatedUser = {
            ...user,
            isSubscribed: selectedPlan.price > 0,
            subscriptionPlan: selectedPlan.name,
            tier: selectedPlan.name,
          };
          setUser(updatedUser);
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }
        
        // Redirect to learning dashboard after successful subscription
        setTimeout(() => {
          navigate('/learning');
        }, 2000); // Give user time to see the success message
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      showAlertModal(
        'Subscription Failed',
        errorMessage,
        'error'
      );
    } finally {
      setSubscribing(null);
    }
  };

  if (loading) {
    // console.log('🔍 Component is loading...');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // console.log('🔍 Component rendering with:', { 
  //   plans: plans.length, 
  //   userSubscription, 
  //   user: user ? 'present' : 'null',
  //   isAuthenticated 
  // });

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">{error}</div>
          <button
            onClick={fetchPlans}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Header Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Unlock premium features and accelerate your learning journey with our flexible subscription plans.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan._id}
              className={`relative bg-white rounded-lg shadow-lg border-2 p-6 flex flex-col h-full ${
                plan.isPopular ? 'border-blue-500' : 'border-gray-200'
              }`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {plan.name}
                </h3>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    {formatPrice(plan.price)}
                  </span>
                  <span className="text-gray-600 ml-1">
                    / {formatDuration(plan.duration)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Perfect for {plan.name.toLowerCase()} users
                </p>
              </div>

              <div className="flex-1 mb-6">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="flex-shrink-0 w-4 h-4 text-green-500 mt-0.5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                className={`w-full py-2 px-4 rounded-md font-medium transition-colors text-sm mt-auto disabled:opacity-50 disabled:cursor-not-allowed ${
                  (() => {
                    // Check subscription data first
                    let isCurrentPlan = (userSubscription && userSubscription.tier === plan.name && userSubscription.isActive) ||
                                     (!userSubscription && plan.name === 'Free');
                    
                    // Fallback: Check user data if subscription data is not available
                    if (!isCurrentPlan && user) {
                      const userSubscriptionPlan = user.subscriptionPlan;
                      const isSubscribed = user.isSubscribed;
                      
                      // If user has an active subscription, check if this plan matches
                      if (isSubscribed && userSubscriptionPlan === plan.name) {
                        isCurrentPlan = true;
                      } else if (!isSubscribed && plan.name === 'Free') {
                        isCurrentPlan = true;
                      }
                    }
                    
                    return isCurrentPlan ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700';
                  })()
                }`}
                onClick={() => handleGetStarted(plan)}
                disabled={(() => {
                  if (subscribing === plan._id) return true;
                  
                  // Check subscription data first
                  let isCurrentPlan = (userSubscription && userSubscription.tier === plan.name && userSubscription.isActive) ||
                                   (!userSubscription && plan.name === 'Free');
                  
                  // Fallback: Check user data if subscription data is not available
                  if (!isCurrentPlan && user) {
                    const userSubscriptionPlan = user.subscriptionPlan;
                    const isSubscribed = user.isSubscribed;
                    
                    // If user has an active subscription, check if this plan matches
                    if (isSubscribed && userSubscriptionPlan === plan.name) {
                      isCurrentPlan = true;
                    } else if (!isSubscribed && plan.name === 'Free') {
                      isCurrentPlan = true;
                    }
                  }
                  
                  return isCurrentPlan;
                })()}
              >
                {subscribing === plan._id ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </div>
                ) : (() => {
                  // Check subscription data first
                  let isCurrentPlan = (userSubscription && userSubscription.tier === plan.name && userSubscription.isActive) || 
                                   (!userSubscription && plan.name === 'Free');
                  
                  // Fallback: Check user data if subscription data is not available
                  if (!isCurrentPlan && user) {
                    const userSubscriptionPlan = user.subscriptionPlan;
                    const isSubscribed = user.isSubscribed;
                    
                    // If user has an active subscription, check if this plan matches
                    if (isSubscribed && userSubscriptionPlan === plan.name) {
                      isCurrentPlan = true;
                    } else if (!isSubscribed && plan.name === 'Free') {
                      isCurrentPlan = true;
                    }
                  }
                  
                  return isCurrentPlan ? 'Current Plan' : 'Get Started';
                })()}
              </button>
            </div>
          ))}
        </div>
      </div>

      <Footer />

      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPlan(null);
        }}
        onConfirm={handlePaymentConfirm}
        planName={selectedPlan?.name || ''}
        planPrice={selectedPlan ? formatPrice(selectedPlan.price) : ''}
        loading={subscribing === selectedPlan?._id}
      />

      <CustomAlert
        isOpen={showAlert}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default Pricing;
